import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { doc, FirestoreError, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { db } from "../lib/firebase";
import { AppFooter } from "../components/AppFooter";
import { GoogleButton } from "../components/GoogleButton";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

function friendlyAuthError(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/email-already-in-use":
        return "Für diese E-Mail existiert bereits ein Konto. Einfach anmelden statt registrieren.";
      case "auth/weak-password":
        return "Das Passwort ist zu schwach (mindestens 6 Zeichen).";
      case "auth/invalid-email":
        return "Diese E-Mail-Adresse ist ungültig.";
      default:
        return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
    }
  }
  return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
}

export function Register() {
  const { register, logout, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  async function handleGoogle() {
    setGoogleError(null);
    try {
      await loginWithGoogle();
    } catch {
      setGoogleError("Google-Anmeldung fehlgeschlagen. Bitte erneut versuchen.");
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const usernameLower = username.trim().toLowerCase();
    if (!USERNAME_RE.test(usernameLower)) {
      setError("Benutzername: 3–20 Zeichen, nur Kleinbuchstaben, Ziffern und Unterstrich.");
      return;
    }
    if (password !== confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setSubmitting(true);
    try {
      const cred = await register(email, password);
      try {
        // Claiming the username doc first means a collision is caught before
        // the profile doc (and thus the account) is fully set up — if it
        // fails, we undo the just-created auth account so the user can
        // simply try again with a different username.
        await setDoc(doc(db, "usernames", usernameLower), { uid: cred.user.uid });
        await setDoc(doc(db, "users", cred.user.uid), {
          username: username.trim(),
          usernameLower,
          displayName: displayName.trim() || username.trim(),
          createdAt: serverTimestamp(),
        });
      } catch (inner) {
        await cred.user.delete().catch(() => {});
        if (inner instanceof FirestoreError && inner.code === "permission-denied") {
          throw new Error("Dieser Benutzername ist schon vergeben. Bitte einen anderen wählen.");
        }
        throw inner;
      }
      navigate("/pair");
    } catch (err) {
      if (err instanceof Error && !(err instanceof FirebaseError)) {
        setError(err.message);
      } else {
        setError(friendlyAuthError(err));
      }
      await logout().catch(() => {});
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-dvh bg-cream-50 dark:bg-night-bg">
      <div className="hero-glow flex flex-col items-center justify-end px-6 pb-14 pt-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-sm">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8 text-cream-50">
            <path d="M12 21s-7.5-4.6-10.3-9.3C.1 9.1 1 5.6 4 4.3c2.3-1 4.7-.1 6 1.8l2 2.9 2-2.9c1.3-1.9 3.7-2.8 6-1.8 3 1.3 3.9 4.8 2.3 7.4C19.5 16.4 12 21 12 21Z" />
          </svg>
        </div>
        <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-cream-50">Konto erstellen</h1>
        <p className="mt-1.5 text-sm text-plum-100/80">Für Zugriff auf Wir</p>
      </div>

      <div className="mx-auto -mt-8 max-w-md px-6 pb-12">
        <form onSubmit={handleSubmit} className="card animate-fade-up space-y-4 rounded-3xl p-6" noValidate>
          <div>
            <label htmlFor="reg-name" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              Dein Name
            </label>
            <input
              id="reg-name"
              type="text"
              required
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
          </div>
          <div>
            <label htmlFor="reg-username" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              Benutzername
            </label>
            <input
              id="reg-username"
              type="text"
              required
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="z.B. mia23"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
            <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">
              Darüber findet dich dein Partner/deine Partnerin später zum Verbinden.
            </p>
          </div>
          <div>
            <label htmlFor="reg-email" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              E-Mail
            </label>
            <input
              id="reg-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
          </div>
          <div>
            <label htmlFor="reg-password" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              Passwort
            </label>
            <input
              id="reg-password"
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
            <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">Mindestens 6 Zeichen.</p>
          </div>
          <div>
            <label htmlFor="reg-confirm" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              Passwort wiederholen
            </label>
            <input
              id="reg-confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-garnet-100 px-3 py-2 text-sm text-garnet-600 dark:bg-garnet-600/15">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-base font-semibold text-cream-50 shadow-sm transition-all hover:bg-plum-800 active:scale-[0.985] disabled:opacity-60"
          >
            {submitting ? "Wird erstellt…" : "Konto erstellen"}
          </button>
        </form>

        <div className="mt-5 flex items-center gap-3 text-xs text-ink-400 dark:text-night-faint">
          <span className="h-px flex-1 bg-cream-200 dark:bg-night-border" />
          oder
          <span className="h-px flex-1 bg-cream-200 dark:bg-night-border" />
        </div>
        <div className="mt-3.5">
          <GoogleButton onClick={handleGoogle} />
        </div>
        {googleError && (
          <p role="alert" className="mt-3 rounded-lg bg-garnet-100 px-3 py-2 text-sm text-garnet-600 dark:bg-garnet-600/15">
            {googleError}
          </p>
        )}

        <Link to="/login" className="mt-6 block text-center text-sm text-ink-400 underline-offset-2 hover:underline dark:text-night-faint">
          Schon ein Konto? Anmelden
        </Link>
        <AppFooter />
      </div>
    </main>
  );
}
